import React from 'react';
import { Alert, Box, Button, ButtonBase, Chip, Grid, IconButton, LinearProgress, Paper, Tooltip as MuiTooltip, Typography, useTheme } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import RefreshIcon from '@mui/icons-material/Refresh';
import InboxIcon from '@mui/icons-material/Inbox';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import BlockIcon from '@mui/icons-material/Block';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import LayersIcon from '@mui/icons-material/Layers';
import DescriptionIcon from '@mui/icons-material/Description';
import StorageIcon from '@mui/icons-material/Storage';
import PercentIcon from '@mui/icons-material/Percent';
import TimerIcon from '@mui/icons-material/Timer';
import EventIcon from '@mui/icons-material/Event';
import dayjs from 'dayjs';
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../../components/page-header';
import { DateField } from '../../../components/form-page-parts';
import { EmptyState } from '../../../components/empty-state';
import { EXTRACTION_STATUS_COLOR, EXTRACTION_STATUS_LABEL, ExtractionStatus, SUBMISSION_STATUS_COLOR, SubmissionStatus, WEEK_DAYS } from '../../../model/ser/ser-enums';
import { formatDate } from '../../../utils/date';
import { formatFileSize } from '../../../utils/format';
import { ClientGate } from '../components/client-gate';
import { useSerClient } from '../components/use-ser-client';
import { SubmissionStatusChip, SubmissionTypeChip } from '../components/submission-chips';
import { useDashboardState } from './context/dashboard-context';
import { useDashboardController } from './context/dashboard-controller';
import { PERIOD_PRESETS, isPeriodValid } from './dashboard-period';
import { ChartCard, KpiCard } from './components/dashboard-cards';

const number = (value?: number) => (value ?? 0).toLocaleString('pt-BR');

// Período da linha do tempo: "YYYY-MM-DD" → "DD/MM"; "YYYY-MM" → "MM/YYYY"; outros como vieram
function formatPeriodLabel(period?: string): string {
    if (!period) return '';
    if (/^\d{4}-\d{2}-\d{2}/.test(period)) return dayjs(period).format('DD/MM');
    if (/^\d{4}-\d{2}$/.test(period)) return dayjs(`${period}-01`).format('MM/YYYY');
    return period;
}

export function DashboardComponent() {
    const theme = useTheme();
    const navigate = useNavigate();
    const client = useSerClient();
    const ctrl = useDashboardController(client.clientId);
    const { data, loading, error, preset, startDate, endDate } = useDashboardState();
    const summary = data?.summary;
    const periodInvalid = !!startDate && !!endDate && !isPeriodValid(startDate, endDate);

    // Clique em um status (cartão ou gráfico): consulta filtrada pelo status e pelas datas de envio do período
    function openStatus(statusId: number) {
        navigate(`/remessas?status=${statusId}&inicio=${startDate}&fim=${endDate}`);
    }

    const statusData = (data?.statusDistribution ?? []).filter((item) => item.count > 0);
    const vehicleData = (data?.vehicleDistribution ?? []).filter((item) => item.count > 0);
    const timeline = (data?.timeline ?? []).map((item) => ({ ...item, label: formatPeriodLabel(item.period) }));
    const extraction = data?.extraction;
    const extractionItems = extraction
        ? [
            { status: ExtractionStatus.Concluida, count: extraction.completed },
            { status: ExtractionStatus.Processando, count: extraction.processing },
            { status: ExtractionStatus.Pendente, count: extraction.pending },
            { status: ExtractionStatus.Erro, count: extraction.error },
            { status: ExtractionStatus.NaoSuportado, count: extraction.notSupported },
        ]
        : [];
    const extractionTotal = extractionItems.reduce((sum, item) => sum + item.count, 0);
    const upcoming = data?.upcomingPublications ?? [];

    const statusCards = [
        { label: 'Novas', value: summary?.new, status: SubmissionStatus.Nova, icon: <FiberNewIcon /> },
        { label: 'Em análise', value: summary?.inAnalysis, status: SubmissionStatus.EmAnalise, icon: <ManageSearchIcon /> },
        { label: 'Pendentes', value: summary?.pending, status: SubmissionStatus.Pendente, icon: <HourglassTopIcon /> },
        { label: 'Processadas', value: summary?.processed, status: SubmissionStatus.Processada, icon: <TaskAltIcon /> },
        { label: 'Canceladas', value: summary?.canceled, status: SubmissionStatus.Cancelada, icon: <BlockIcon /> },
    ];

    return (
        <React.Fragment>
            <Grid item xs={12}>
                <PageHeader
                    title="Dashboard"
                    subtitle="Acompanhe os envios de remessas de publicação"
                    breadcrumbs={[{ label: 'Dashboard' }]}
                    actions={<Button variant="contained" startIcon={<SendIcon />} onClick={() => navigate('/enviar-remessa')}>Enviar remessa</Button>}
                />
            </Grid>

            <ClientGate isClientUser={client.isClientUser} clientId={client.clientId} onChange={client.setClientId} description="para ver o painel de envios">
                <Grid item xs={12}>
                    <Paper sx={{ p: 2, display: 'flex', gap: 2, alignItems: { xs: 'stretch', md: 'flex-end' }, flexDirection: { xs: 'column', md: 'row' } }}>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', flex: 1, alignItems: 'center' }}>
                            {PERIOD_PRESETS.map((item) => (
                                <Chip
                                    key={item.value}
                                    label={item.label}
                                    color={preset === item.value ? 'primary' : 'default'}
                                    variant={preset === item.value ? 'filled' : 'outlined'}
                                    onClick={() => ctrl.selectPreset(item.value)}
                                />
                            ))}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end' }}>
                            <Box sx={{ width: 160 }}><DateField label="De" value={startDate} onChange={(v) => ctrl.changeDate('startDate', v)} /></Box>
                            <Box sx={{ width: 160 }}><DateField label="Até" value={endDate} error={periodInvalid && 'Data final anterior à inicial'} onChange={(v) => ctrl.changeDate('endDate', v)} /></Box>
                            <MuiTooltip title="Atualizar">
                                <span><IconButton onClick={ctrl.load} disabled={loading || periodInvalid} aria-label="Atualizar" sx={{ mb: periodInvalid ? 3 : 0.5 }}><RefreshIcon /></IconButton></span>
                            </MuiTooltip>
                        </Box>
                    </Paper>
                    {loading && <LinearProgress sx={{ mt: -0.5, borderRadius: 1 }} />}
                </Grid>

                {error && (
                    <Grid item xs={12}>
                        <Alert severity="error" action={<Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={ctrl.load}>Tentar novamente</Button>}>
                            {error}
                        </Alert>
                    </Grid>
                )}

                <Grid item xs={12}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={4} lg={2}>
                            <KpiCard label="Total de remessas" value={number(summary?.total)} icon={<InboxIcon />} color={theme.palette.primary.main} hint={`${number(summary?.batches)} lotes`} onClick={() => navigate(`/remessas?inicio=${startDate}&fim=${endDate}`)} />
                        </Grid>
                        {statusCards.map((card) => (
                            <Grid item xs={6} sm={4} md={4} lg={2} key={card.status}>
                                <KpiCard label={card.label} value={number(card.value)} icon={card.icon} color={SUBMISSION_STATUS_COLOR[card.status].hex} onClick={() => openStatus(card.status)} />
                            </Grid>
                        ))}
                        <Grid item xs={6} sm={4} md={4} lg={2}>
                            <KpiCard label="Urgentes" value={number(summary?.urgent)} icon={<PriorityHighIcon />} color={theme.palette.warning.main} />
                        </Grid>
                        <Grid item xs={6} sm={4} md={4} lg={2}>
                            <KpiCard label="Lotes" value={number(summary?.batches)} icon={<LayersIcon />} color={theme.palette.secondary.main} />
                        </Grid>
                        <Grid item xs={6} sm={4} md={4} lg={2}>
                            <KpiCard label="Documentos" value={number(summary?.documents)} icon={<DescriptionIcon />} color={theme.palette.info.main} />
                        </Grid>
                        <Grid item xs={6} sm={4} md={4} lg={2}>
                            <KpiCard label="Volume enviado" value={formatFileSize(summary?.totalSizeBytes ?? 0)} icon={<StorageIcon />} color={theme.palette.info.dark} />
                        </Grid>
                        <Grid item xs={6} sm={4} md={4} lg={2}>
                            <KpiCard
                                label="Taxa de cancelamento"
                                value={`${(summary?.cancellationRate ?? 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`}
                                icon={<PercentIcon />}
                                color={theme.palette.error.main}
                            />
                        </Grid>
                        <Grid item xs={6} sm={4} md={4} lg={2}>
                            <KpiCard
                                label="Tempo médio"
                                value={summary?.averageProcessingHours === undefined || summary?.averageProcessingHours === null ? '—' : `${summary.averageProcessingHours.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} h`}
                                hint="do envio ao processamento"
                                icon={<TimerIcon />}
                                color={theme.palette.success.main}
                            />
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item xs={12} md={8}>
                    <ChartCard title="Evolução dos envios" subtitle="Remessas enviadas, processadas e canceladas no período" empty={timeline.length === 0}>
                        <ResponsiveContainer width="100%" height={280}>
                            <AreaChart data={timeline} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="serSent" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={theme.palette.primary.main} stopOpacity={0.35} />
                                        <stop offset="100%" stopColor={theme.palette.primary.main} stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme.palette.divider} />
                                <XAxis dataKey="label" tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                                <Tooltip />
                                <Legend />
                                <Area type="monotone" dataKey="sent" name="Enviadas" stroke={theme.palette.primary.main} strokeWidth={2} fill="url(#serSent)" />
                                <Area type="monotone" dataKey="processed" name="Processadas" stroke={SUBMISSION_STATUS_COLOR[SubmissionStatus.Processada].hex} strokeWidth={2} fill="transparent" />
                                <Area type="monotone" dataKey="canceled" name="Canceladas" stroke={SUBMISSION_STATUS_COLOR[SubmissionStatus.Cancelada].hex} strokeWidth={2} fill="transparent" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </Grid>

                <Grid item xs={12} md={4}>
                    <ChartCard title="Por status" subtitle="Clique em um status para ver as remessas" empty={statusData.length === 0}>
                        <ResponsiveContainer width="100%" height={200}>
                            <PieChart>
                                <Pie
                                    data={statusData.map((item) => ({ id: item.id, name: item.name ?? '', count: item.count }))}
                                    dataKey="count"
                                    nameKey="name"
                                    innerRadius={55}
                                    outerRadius={85}
                                    paddingAngle={2}
                                    cursor="pointer"
                                    onClick={(_: unknown, index: number) => openStatus(statusData[index].id)}
                                >
                                    {statusData.map((item) => (
                                        <Cell key={item.id} fill={SUBMISSION_STATUS_COLOR[item.id as SubmissionStatus]?.hex ?? theme.palette.grey[500]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                            {statusData.map((item) => (
                                <ButtonBase key={item.id} onClick={() => openStatus(item.id)} sx={{ justifyContent: 'space-between', px: 1, py: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: SUBMISSION_STATUS_COLOR[item.id as SubmissionStatus]?.hex ?? 'grey.500' }} />
                                        <Typography variant="body2">{item.name}</Typography>
                                    </Box>
                                    <Typography variant="body2" fontWeight={700}>
                                        {number(item.count)} <Typography component="span" variant="caption" color="text.secondary">({item.percentage.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%)</Typography>
                                    </Typography>
                                </ButtonBase>
                            ))}
                        </Box>
                    </ChartCard>
                </Grid>

                <Grid item xs={12} md={6}>
                    <ChartCard title="Por veículo de publicação" empty={vehicleData.length === 0}>
                        <ResponsiveContainer width="100%" height={Math.max(200, vehicleData.length * 44)}>
                            <BarChart data={vehicleData} layout="vertical" margin={{ top: 0, right: 24, left: 8, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={theme.palette.divider} />
                                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                                <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                                <Tooltip />
                                <Bar dataKey="count" name="Remessas" fill={theme.palette.primary.main} radius={[0, 4, 4, 0]} barSize={20} />
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </Grid>

                <Grid item xs={12} md={6}>
                    <ChartCard title="Extração de conteúdo" subtitle="Situação da leitura de texto dos documentos enviados" empty={extractionTotal === 0} height={200}>
                        <Box sx={{ display: 'flex', height: 14, borderRadius: 7, overflow: 'hidden', bgcolor: 'action.hover' }}>
                            {extractionItems.filter((item) => item.count > 0).map((item) => (
                                <MuiTooltip key={item.status} title={`${EXTRACTION_STATUS_LABEL[item.status]}: ${number(item.count)}`}>
                                    <Box sx={{ width: `${(item.count * 100) / extractionTotal}%`, bgcolor: EXTRACTION_STATUS_COLOR[item.status].hex }} />
                                </MuiTooltip>
                            ))}
                        </Box>
                        <Grid container spacing={1.5} sx={{ mt: 1.5 }}>
                            {extractionItems.map((item) => (
                                <Grid item xs={6} sm={4} key={item.status}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: EXTRACTION_STATUS_COLOR[item.status].hex }} />
                                        <Typography variant="body2" color="text.secondary">{EXTRACTION_STATUS_LABEL[item.status]}</Typography>
                                        <Typography variant="body2" fontWeight={700} sx={{ ml: 'auto' }}>{number(item.count)}</Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                        {(extraction?.fileTypes?.length ?? 0) > 0 && (
                            <Box sx={{ mt: 2.5 }}>
                                <Typography variant="caption" color="text.secondary" fontWeight={600}>Tipos de arquivo</Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.75 }}>
                                    {extraction!.fileTypes!.map((type) => <Chip key={type.id} size="small" variant="outlined" label={`${type.name} · ${number(type.count)}`} />)}
                                </Box>
                            </Box>
                        )}
                    </ChartCard>
                </Grid>

                <Grid item xs={12}>
                    <Paper sx={{ p: 2.5 }}>
                        <Typography variant="subtitle1" fontWeight={700}>Próximas publicações</Typography>
                        <Typography variant="caption" color="text.secondary">Remessas com publicação agendada a partir de hoje</Typography>
                        <Box sx={{ mt: 2 }}>
                            {upcoming.length === 0 && <EmptyState icon={<EventIcon />} message="Nenhuma publicação agendada." actionLabel="Enviar remessa" onAction={() => navigate('/enviar-remessa')} />}
                            {upcoming.map((item, index) => {
                                const date = dayjs(item.publishDate);
                                return (
                                    <ButtonBase
                                        key={item.id}
                                        onClick={() => navigate(`/remessas?id=${item.id}`)}
                                        sx={{
                                            width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 2, py: 1.25, px: 1,
                                            borderTop: index ? '1px solid' : 'none', borderColor: 'divider', borderRadius: 1,
                                            '&:hover': { bgcolor: 'action.hover' },
                                        }}
                                    >
                                        <Box sx={{ width: 52, flexShrink: 0, textAlign: 'center', py: 0.5, borderRadius: 1.5, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                                            <Typography variant="caption" component="div" sx={{ lineHeight: 1.2, textTransform: 'uppercase', opacity: 0.85 }}>{WEEK_DAYS[date.day()].short}</Typography>
                                            <Typography variant="subtitle1" component="div" fontWeight={700} sx={{ lineHeight: 1.1 }}>{date.format('DD/MM')}</Typography>
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="body2" fontWeight={600} noWrap>{item.title}</Typography>
                                            <Typography variant="caption" color="text.secondary">Remessa nº {item.id} · publicação em {formatDate(item.publishDate)}</Typography>
                                        </Box>
                                        <SubmissionTypeChip type={item.type} compact />
                                        <SubmissionStatusChip statusId={item.statusId} label={item.status} />
                                    </ButtonBase>
                                );
                            })}
                        </Box>
                    </Paper>
                </Grid>
            </ClientGate>
        </React.Fragment>
    );
}
